import GeoJSON from "ol/format/GeoJSON.js";
import { LineString } from "ol/geom";
import { fromLonLat, toLonLat } from "ol/proj";
import proj4 from "proj4";
import { clone } from "@turf/turf";
import {
  getConverter,
  InterpolationSpecType,
  InterpolationType,
  lengthOnPlaneCm,
  LRChainEncoded,
  LRComponents,
  LRDecoder,
  LRSegment,
  Reinterpolator,
} from "@orbis-lr-sdk/orbis-lr-sdk";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { lineSlice } from "@turf/line-slice";

export function featureTo4326Geometry(feature) {
  const format = new GeoJSON();
  const geometry = format.writeFeatureObject(feature, {
    featureProjection: "EPSG:3857",
    dataProjection: "EPSG:4326",
  }).geometry;
  return geometry;
}

export function toLatLon(geometry) {
  const proj = proj4("EPSG:3857", "EPSG:4326");
  const latLonGeometry = clone(geometry);
  if (is2DArray(geometry.coordinates)) {
    latLonGeometry.coordinates = geometry.coordinates.map((coordinate) =>
      proj.forward(coordinate),
    );
  } else {
    latLonGeometry.coordinates = proj.forward(geometry.coordinates);
  }
  return latLonGeometry;
}
function is2DArray(arr) {
  return Array.isArray(arr) && arr.every((element) => Array.isArray(element));
}
export function distanceAlongCm(latLonGeometry, latLonEventPoint) {
  const converter = getConverter(latLonGeometry);
  const closestPoint = nearestPointOnLine(latLonGeometry, latLonEventPoint);
  const sliceTurfFeature = lineSlice(
    latLonGeometry.coordinates[0],
    closestPoint,
    latLonGeometry,
  );
  const lengthCm = lengthOnPlaneCm(sliceTurfFeature.geometry, converter);
  return lengthCm;
}
export function getCoordinateAtCm(olGeometry, distanceCm, lengthCm) {
  if (lengthCm <= 0) return olGeometry.getCoordinateAt(0);
  const fraction = Math.max(0, Math.min(1, distanceCm / lengthCm));
  return olGeometry.getCoordinateAt(fraction);
}

// Decode a relation member role ("key=value") into the distance ranges (cm)
// along that member's own geometry where the member participates in the
// relation. Distances are measured on the orbis-lr-sdk Gauss-Krüger plane —
// the same metric as distanceAlongCm() and ValueAtProducer.getValueAt(ROLE).
export function getRoleRangesCm(role) {
  if (typeof role !== "string" || !role.includes("=")) return [];
  const [key, value] = role.split("=", 2);
  let chain;
  try {
    chain = new LRDecoder()
      .withInterpolationSpecType(InterpolationSpecType.ROLE)
      .decode(key, value);
  } catch {
    return [];
  }
  return chain.elements
    .map((element) => {
      const endCm =
        typeof element.getEndDistance === "function"
          ? element.getEndDistance()
          : undefined;
      return {
        startCm: element.getStartDistance(),
        // The LR spec allows the end to be omitted: the member is then involved
        // from this offset till the end of the segment (decoded as an open-ended
        // LROffset). Represent that as Infinity — sliceOlLineByCm clamps it to
        // the geometry's actual length.
        endCm: endCm === undefined || endCm === null ? Infinity : endCm,
      };
    })
    .filter((range) => range.endCm > range.startCm);
}

// Slice a [lon, lat] coordinate array between two cm offsets using the
// orbis-lr-sdk Gauss-Krüger plane distance (same metric as getRoleRangesCm /
// distanceAlongCm). Walks segment-by-segment and interpolates the cut points.
export function sliceLonLatLineByCm(coords, startCm, endCm) {
  if (coords.length < 2) return coords.slice();
  const midCoord = coords[Math.floor(coords.length / 2)];
  const converter = getConverter({
    type: "LineString",
    coordinates: [midCoord, midCoord],
  });
  const projected = coords.map((c) =>
    converter.convertWgsToGaussKrueger(c[0], c[1]),
  );
  const cumDistCm = [0];
  for (let i = 1; i < projected.length; i++) {
    const dx = projected[i][0] - projected[i - 1][0];
    const dy = projected[i][1] - projected[i - 1][1];
    cumDistCm.push(cumDistCm[i - 1] + Math.sqrt(dx * dx + dy * dy) * 100);
  }
  const totalCm = cumDistCm[cumDistCm.length - 1];
  const clampedStart = Math.max(0, Math.min(startCm, totalCm));
  const clampedEnd = Math.max(clampedStart, Math.min(endCm, totalCm));
  const result = [interpolateLonLatAtCm(coords, cumDistCm, clampedStart)];
  for (let i = 0; i < coords.length; i++) {
    if (cumDistCm[i] > clampedStart && cumDistCm[i] < clampedEnd) {
      result.push(coords[i]);
    }
  }
  result.push(interpolateLonLatAtCm(coords, cumDistCm, clampedEnd));
  return result;
}

// Slice an OL LineString geometry (EPSG:3857) to a [startCm, endCm] range,
// returning a new EPSG:3857 LineString. Distances are measured on the GK plane.
export function sliceOlLineByCm(olLineString, startCm, endCm) {
  const lonLat = olLineString.getCoordinates().map((c) => toLonLat(c));
  const sliced = sliceLonLatLineByCm(lonLat, startCm, endCm);
  return new LineString(sliced.map((c) => fromLonLat(c)));
}

function interpolateLonLatAtCm(coords, cumDistCm, targetCm) {
  if (targetCm <= 0) return coords[0];
  if (targetCm >= cumDistCm[cumDistCm.length - 1]) {
    return coords[coords.length - 1];
  }
  for (let i = 1; i < cumDistCm.length; i++) {
    if (cumDistCm[i] >= targetCm) {
      const segLen = cumDistCm[i] - cumDistCm[i - 1];
      if (segLen === 0) return coords[i - 1];
      const t = (targetCm - cumDistCm[i - 1]) / segLen;
      return [
        coords[i - 1][0] + t * (coords[i][0] - coords[i - 1][0]),
        coords[i - 1][1] + t * (coords[i][1] - coords[i - 1][1]),
      ];
    }
  }
  return coords[coords.length - 1];
}

// Lateral-acceleration-derived speed (km/h) from a TomTom curvature value.
// radiusMeters = 10000 / |curvature|. Capped at 130 km/h.
export function maxSpeedFromCurvature(curvature) {
  if (curvature === 0) {
    return 130;
  }
  const maxLateralAccelerationG = 0.3;
  const radius = 10000 / Math.abs(curvature);
  const maxSpeedMs = Math.sqrt(maxLateralAccelerationG * 9.81 * radius);
  return Math.min(Math.round(maxSpeedMs * 3.6), 130);
}

// Display rows for a `curvature_at_junction` value — the same three rows the
// WKT viewer renders on hover. Empty array if missing or zero.
export function curvatureAtJunctionKeyValues(curvatureAtJunction) {
  if (curvatureAtJunction === undefined || curvatureAtJunction === null) {
    return [];
  }
  const curvature = Number(curvatureAtJunction);
  if (curvature === 0 || isNaN(curvature)) return [];
  const radius = 10000 / Math.abs(curvature);
  return [
    ["curvature_at_junction", curvature],
    ["radius", Math.round(radius) + " m"],
    [
      "max speed (based on curvature)",
      maxSpeedFromCurvature(curvature) + " km/h",
    ],
  ];
}
export function rowHeader(value) {
  const tr = document.createElement("tr");
  const cell = document.createElement("th");
  cell.colSpan = 2;
  if (typeof value === "object") {
    cell.appendChild(value);
  } else {
    cell.textContent = value;
  }
  tr.append(cell);
  return tr;
}
export function rowHint(value) {
  const tr = document.createElement("tr");
  const cell = document.createElement("td");
  cell.colSpan = 2;
  cell.className = "hint";
  cell.textContent = value;
  tr.append(cell);
  return tr;
}
export function row(label, value) {
  const tr = document.createElement("tr");
  const cellLabel = document.createElement("td");
  cellLabel.classList.add("label");
  const cellValue = document.createElement("td");
  cellValue.classList.add("value");
  if (typeof label === "object") {
    cellLabel.appendChild(label);
  } else {
    cellLabel.textContent = label;
  }
  if (typeof value === "object") {
    cellValue.appendChild(value);
  } else {
    cellValue.textContent = value;
  }
  tr.append(cellLabel);
  tr.append(cellValue);
  return tr;
}
export function getTagValueAt(tagKey, tagValue, lengthCm) {
  if (tagValue === undefined) {
    return undefined;
  }
  const lrComponents = LRComponents.builder()
    .segment(
      LRSegment.builder()
        .withStartDistance(0)
        .withEndDistance(lengthCm)
        .build(),
    )
    .build();
  const reinterpolated = new Reinterpolator().reinterpolate(
    lrComponents,
    new LRChainEncoded(tagKey, tagValue),
  );
  const reinterpolatedSegment = reinterpolated.segments[0];
  let lookupTagKey;
  if (reinterpolatedSegment.attributes.has(tagKey)) {
    lookupTagKey = tagKey;
  } else {
    const interpolationType = InterpolationType.fromValue(tagKey);
    const baseTagKey = tagKey.replace(new RegExp(`:${interpolationType}$`), "");
    if (reinterpolatedSegment.attributes.has(baseTagKey)) {
      lookupTagKey = baseTagKey;
    } else {
      return "";
    }
  }
  const lrChainLRChains = new LRDecoder()
    .withInterpolationSpecType(InterpolationSpecType.TAG)
    .withKey(lookupTagKey)
    .withValue(reinterpolatedSegment.attributes.get(lookupTagKey))
    .decode();
  return lrChainLRChains.getChains()[0].elements.slice(-1)[0].value;
}
export function getMembers(feature) {
  const members = [];
  let i = 0;
  while (feature.get(`relation-member-${i}-id`) !== undefined) {
    const id = feature.get(`relation-member-${i}-id`);
    const role = feature.get(`relation-member-${i}-role`);
    const member = { id, role };

    const lrStart = feature.get(`relation-member-${i}-lr-start`);
    const lrEnd = feature.get(`relation-member-${i}-lr-end`);
    const lrType = feature.get(`relation-member-${i}-lr-type`);

    if (lrStart !== undefined || lrEnd !== undefined || lrType !== undefined) {
      member.lrRange = {};
      if (lrStart !== undefined) member.lrRange.start = Number(lrStart);
      if (lrEnd !== undefined) member.lrRange.end = Number(lrEnd);
      if (lrType !== undefined) member.lrRange.type = lrType;
    }
    members.push(member);
    i++;
  }
  return members;
}

export function getMembersWithRole(feature, requestedRole) {
  return getMembers(feature).filter((member) => member.role === requestedRole);
}

export function getTags(feature) {
  const properties = feature.getProperties();
  return Object.entries(properties).reduce((acc, [property, tagValue]) => {
    const match = property.match(/^(\d+)-(.+)$/);
    if (match) {
      const layerId = parseInt(match[1], 10);
      const key = match[2];
      const revision = properties[`revision-${property}`];
      acc.push({
        tagKey: {
          layerId,
          key,
        },
        tagValue: tagValue,
        revision: revision ? parseInt(revision, 10) : null,
      });
    }
    return acc;
  }, []);
}
